// Zweck: Belegfassungen nur lesen; keine Positionen oder Mengen in Buchungen übernehmen. Stand: 22.10.2026.
import { useState } from 'react';
import type { WarehouseDocument } from '../interfaces/warehouseDocuments';
import { simMatches } from '../services/deviceSimulation';

export default function WarehouseDocumentPicker({
  documents,
}: {
  documents: WarehouseDocument[];
}) {
  const [selected, setSelected] = useState('');
  const [query, setQuery] = useState('');
  const key = (document: WarehouseDocument) =>
    `${document.id}:${document.version}`;
  const document = documents.find((d) => key(d) === selected);
  const lines =
    document?.lines.filter((line) =>
      simMatches(
        `${line.position} ${line.name} ${line.articleNumber ?? ''} ${line.description ?? ''}`,
        query,
      ),
    ) ?? [];
  return (
    <details className="wh-document-picker">
      <summary>Belege ansehen ({documents.length})</summary>
      <p className="wh-field-hint">
        Nur zum Nachschlagen. Artikel anschließend über „Artikel hinzufügen“
        selbst zusammenstellen und Mengen prüfen.
      </p>
      {!documents.length ? (
        <p>Für dieses Projekt ist noch kein Beleg geladen.</p>
      ) : (
        <label>
          Belegfassung
          <select
            value={selected}
            onChange={(e) => {
              setSelected(e.target.value);
              setQuery('');
            }}
          >
            <option value="">Beleg auswählen</option>
            {documents.map((d) => (
              <option key={key(d)} value={key(d)}>
                {d.type === 'confirmation' ? 'Auftragsbestätigung' : 'Angebot'}{' '}
                {d.number} · Fassung {d.version} · {d.status}
              </option>
            ))}
          </select>
        </label>
      )}
      {document && (
        <div className="wh-document">
          <h3>
            {document.number} · Fassung {document.version}
          </h3>
          <p className="wh-field-hint">
            {document.date.split('-').reverse().join('.')} · {document.status}
          </p>
          {document.simulated && (
            <p className="wh-notice">
              Lokaler Testbeleg. Kein echter HERO-Beleg.
            </p>
          )}
          <p className="wh-field-hint">
            Gespeicherte Positionsansicht ohne Preise. Kein Original-PDF.
            Angaben und Paketmengen selbst prüfen.
          </p>
          {document.warning && <p className="wh-notice">{document.warning}</p>}
          <label>
            Im Beleg suchen
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Artikelnummer oder Bezeichnung"
            />
          </label>
          {lines.map((line, index) => (
            <div className="wh-document-position" key={index}>
              <strong>
                {line.position} · {line.name}
              </strong>
              <p>
                {line.quantity.toLocaleString('de-DE')} {line.unit}
              </p>
              {line.articleNumber && <p>Artikelnr. {line.articleNumber}</p>}
              {line.description && <p>{line.description}</p>}
              {line.manufacturer && <p>Hersteller: {line.manufacturer}</p>}
              {line.manufacturerNumber && (
                <p>Herstellernr. {line.manufacturerNumber}</p>
              )}
              {line.barcode && <p>Barcode: {line.barcode}</p>}
            </div>
          ))}
          {!lines.length && (
            <p>
              {query
                ? 'Keine passende Position. Suche ändern.'
                : 'Dieser Beleg enthält keine Positionen.'}
            </p>
          )}
        </div>
      )}
    </details>
  );
}
